import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import '../../css/forms/ApplicationForms.css';

const API_URL = 'https://rm-backend-service-505761517154.us-central1.run.app/submit-acorns-ambassador';

const AcornsAmbassador = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    university: '',
    graduation_year: '',
    ambassador_type: '',
    college_address: '',
    home_address: '',
    work_status: '',
    securities_firm: '',
    past_experience: '',
    interest_reason: '',
    additional_notes: '',
    resume_url: '',
    instagram_url: '',
    tiktok_url: '',
    cover_letter_url: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 1400);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (submitAttempted && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      'first_name', 'last_name', 'email', 'phone', 
      'university', 'graduation_year', 'ambassador_type','college_address',
      'home_address', 'work_status', 'securities_firm', 'past_experience', 'interest_reason',
      'resume_url', 'instagram_url', 'tiktok_url'
    ];

    requiredFields.forEach(field => {
      if (!formData[field] || (typeof formData[field] === 'string' && !formData[field].trim())) {
        errors[field] = 'This field is required';
      }
    });

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // if (formData.resume_url && !/^https?:\/\/.+\..+/.test(formData.resume_url)) {
    //   errors.resume_url = 'Please enter a valid URL';
    // }

    // if (formData.cover_letter_url && !/^https?:\/\/.+\..+/.test(formData.cover_letter_url)) {
    //   errors.cover_letter_url = 'Please enter a valid URL';
    // }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitAttempted(true);
  setIsSubmitting(true);
  window.scrollTo({ top: 1400, behavior: 'smooth' });

  if (validateForm()) {
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const contentType = response.headers.get("content-type");

      if (!response.ok) {
        // Try to parse error if it's JSON, otherwise fallback to text
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to submit application');
        } else {
          const errorText = await response.text();
          throw new Error(`Server error: ${errorText.slice(0, 100)}...`);
        }
      }

      const data = await response.json(); // Safe to parse
      console.log('Success:', data);
      setSubmitted(true);
      setShowThankYouModal(true);
      window.scrollTo(0, 1400);

    } catch (error) {
      console.error('Error submitting form:', error.message);
      alert(error.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  } else {
    setIsSubmitting(false);
    const firstErrorField = document.querySelector('.error');
    if (firstErrorField) {
      firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
};


  const closeModal = () => {
    setShowThankYouModal(false);
    navigate('/roles');
    window.scrollTo(0, 0);
  };

  if (submitted) {
    return (
      <div className="aam-container thank-you-page">
        <Helmet>
          <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" 
          rel="stylesheet" />
        </Helmet>
        
        {showThankYouModal && (
          <div className="thank-you-modal">
            <div className="thank-you-content">
              <h1>Thank You for Applying!</h1>
              <p>We've received your application for the Acorns Campus Ambassador position.</p>
              <p>If we feel you're a good fit for the role, we'll email you with next steps soon.</p>
              <button onClick={closeModal}>Go to Roles</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="aam-container">
      <Helmet>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" 
        rel="stylesheet" />
      </Helmet>
      
      <div className="aam-header">
        <h1>Acorns Campus Ambassador</h1>
        <div className="aam-meta">
          <span className="meta-item">Location: Your Campus</span>
          <span className="meta-item">Job Type: Part-Time</span>
        </div>
      </div>

      <div className="aam-content">
        <section className="aam-section aam-about">
          <h2>About Acorns</h2>
          <p>Acorns, one of the fastest-growing FinTech companies, is on a mission 
            to spread financial literacy across college campuses, and we want you to
            be a part of it! We're looking for dynamic, motivated students to join 
            our team as field marketers and brand ambassadors. Learn more at <a href="https://acorns.com">Acorns.com.</a></p>
        </section>

        <section className="aam-section aam-description">
          <h2>Why Join Us?</h2>
          <ul className="aam-list">
            <li><strong>Exclusive Opportunity:</strong> Only 2 positions available per campus!</li>
            <li><strong>Make an Impact:</strong> Help your peers achieve financial wellness.</li>
            <li><strong>Professional Growth:</strong> Gain valuable experience in marketing and networking.</li>
          </ul>
        </section>

        <section className="aam-section aam-responsibilities">
          <h2>Position Details</h2>
          <p>As an Acorns Campus Ambassador, you'll be the face of our brand, driving grassroots marketing 
            initiatives. 
            <strong> There are two types of ambassadors: social leads (social media focus) and events leads (in-person events
            focus).
            </strong></p>
            <p><strong>
              Note: Social Ambassadors need to have at least 2k followers on their social platforms.
            </strong></p>
          <h3>Your responsibilities will include:</h3>
          <ul className="aam-list">
            <li><strong>Time Commitment:</strong> 20 hours/month</li>
            <li><strong>Role Length:</strong> August 1 - December 20</li>
            <li><strong>Effective Communication:</strong> Engage and educate fellow students about Acorns.</li>
            <li><strong>Event Identification:</strong> Spot and suggest potential sponsorship opportunities.</li>
            <li><strong>Event Coordination:</strong> Organize and host exciting tabling events to promote 
            Acorns.</li>
            <li>Note: The program is open only to current freshmen, sophomores, or juniors.</li>
          </ul>
        </section>

        <section className="aam-section aam-benefits">
          <h2>What's in it for you?</h2>
          <ul className="aam-list">
            <li><strong>Exclusive Merch:</strong> Get your hands on brand new, first-time Acorns merchandise!</li>
            <li><strong>Career Growth:</strong> Explore opportunities for advancement within Acorns.</li>
            <li><strong>Networking:</strong> Connect with industry professionals and fellow ambassadors.</li>
          </ul>
        </section>

        <section className="aam-section aam-application">
          <h2>How to Apply</h2>
          <p>If you're passionate about financial literacy and ready to make a difference on your campus, apply now! We'll reach out if you're a good fit.
            If you have any questions, please email <b>elisa@romulusmedia.com</b> .
          </p>
          <p className="aam-emphasis">This opportunity is presented by Romulus Media.</p>
        </section>

        <form onSubmit={handleSubmit} className="aam-form">
          <h2 className="form-title">Application Form</h2>
          
          {submitAttempted && Object.keys(formErrors).length > 0 && (
            <div className="form-error-message">
              Please correct the highlighted errors:
            </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label className="question-text">First Name *</label>
              <input 
                type="text" 
                name="first_name" 
                value={formData.first_name} 
                onChange={handleChange} 
                className={formErrors.first_name ? 'error' : ''}
              />
              {formErrors.first_name && <span className="error-message">{formErrors.first_name}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Last Name *</label>
              <input 
                type="text" 
                name="last_name" 
                value={formData.last_name} 
                onChange={handleChange} 
                className={formErrors.last_name ? 'error' : ''}
              />
              {formErrors.last_name && <span className="error-message">{formErrors.last_name}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">University Email *</label>
              <input 
                type="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                className={formErrors.email ? 'error' : ''}
              />
              {formErrors.email && <span className="error-message">{formErrors.email}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Phone *</label>
              <input 
                type="tel" 
                name="phone" 
                value={formData.phone} 
                onChange={handleChange} 
                className={formErrors.phone ? 'error' : ''}
              />
              {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">University/College Name *</label>
              <input 
                type="text" 
                name="university" 
                value={formData.university} 
                onChange={handleChange} 
                className={formErrors.university ? 'error' : ''}
              />
              {formErrors.university && <span className="error-message">{formErrors.university}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Expected Year of Graduation *</label>
              <input 
                type="text" 
                name="graduation_year" 
                value={formData.graduation_year} 
                onChange={handleChange} 
                className={formErrors.graduation_year ? 'error' : ''}
              />
              {formErrors.graduation_year && <span className="error-message">{formErrors.graduation_year}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Would you prefer to be a social ambassador or an events ambassador? *</label>
              <input 
                type="text" 
                name="ambassador_type" 
                value={formData.ambassador_type} 
                onChange={handleChange} 
                className={formErrors.ambassador_type ? 'error' : ''}
              />
              {formErrors.ambassador_type && <span className="error-message">{formErrors.ambassador_type}</span>}
            </div>
            
            <div className="form-group full-width">
              <label className="question-text">What is your college address - dorm, apartment, housing, etc.? *</label>
              <input 
                type="text" 
                name="college_address" 
                value={formData.college_address} 
                onChange={handleChange} 
                className={formErrors.college_address ? 'error' : ''}
              />
              {formErrors.college_address && <span className="error-message">{formErrors.college_address}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">What is your home (non-college) address? This is for compliance regulations as Acorns is a FDIC-insured platform. *</label>
              <input 
                type="text" 
                name="home_address" 
                value={formData.home_address} 
                onChange={handleChange} 
                className={formErrors.home_address ? 'error' : ''}
              />
              {formErrors.home_address && <span className="error-message">{formErrors.home_address}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Are you a U.S. citizen or authorized to work in the United States? (please note that if the answer is "No", you will still be considered.) *</label>
              <div className="radio-group">
                <label className="label-select">
                  <input
                    type="radio"
                    name="work_status"
                    value="yes"
                    checked={formData.work_status === "yes"}
                    onChange={handleChange}
                  />
                  Yes
                </label>
                <label className="label-select">
                  <input
                    type="radio"
                    name="work_status"
                    value="no"
                    checked={formData.work_status === "no"}
                    onChange={handleChange}
                  />
                  No
                </label>
              </div>
              {formErrors.work_status && <span className="error-message">{formErrors.work_status}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Have you ever been registered with a securities firm, and, if so, the name of firm(s)? *</label>
              <input 
                type="text" 
                name="securities_firm" 
                value={formData.securities_firm} 
                onChange={handleChange} 
                className={formErrors.securities_firm ? 'error' : ''}
              />
              {formErrors.securities_firm && <span className="error-message">{formErrors.securities_firm}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Have you previously served as a campus ambassador? 
                If yes, which company or organization was it for, and what were your main responsibilities? *</label>
              <textarea 
                name="past_experience" 
                value={formData.past_experience} 
                onChange={handleChange} 
                className={formErrors.past_experience ? 'error' : ''}
                rows="3"
              />
              {formErrors.past_experience && <span className="error-message">{formErrors.past_experience}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">What clubs, organizations, or communities on campus are you involved in, 
                and how would you leverage those for this role? *</label>
              <textarea 
                name="interest_reason" 
                value={formData.interest_reason} 
                onChange={handleChange} 
                className={formErrors.interest_reason ? 'error' : ''}
                rows="3"
              />
              {formErrors.interest_reason && <span className="error-message">{formErrors.interest_reason}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Resume URL * (Google Drive, Dropbox, etc.)</label>
              <input 
                type="text" 
                name="resume_url" 
                value={formData.resume_url} 
                onChange={handleChange} 
                placeholder="drive.google.com/your-resume"
                className={formErrors.resume_url ? 'error' : ''}
              />
              {formErrors.resume_url && <span className="error-message">{formErrors.resume_url}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Cover Letter + references URL (optional). For multiple
                links, separate with commas.
              </label>
              <input 
                type="text" 
                name="cover_letter_url" 
                value={formData.cover_letter_url} 
                onChange={handleChange} 
                placeholder="drive.google.com/your-cover-letter"
              />
            </div>

            <div className="form-group full-width">
              <label className="question-text">Instagram URL *</label>
              <input 
                type="text" 
                name="instagram_url" 
                value={formData.instagram_url} 
                onChange={handleChange} 
                placeholder="instagram.com/your-username"
                className={formErrors.instagram_url ? 'error' : ''}
              />
              {formErrors.instagram_url && <span className="error-message">{formErrors.instagram_url}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">TikTok URL *</label>
              <input 
                type="text" 
                name="tiktok_url" 
                value={formData.tiktok_url} 
                onChange={handleChange} 
                placeholder="tiktok.com/@your-username"
                className={formErrors.tiktok_url ? 'error' : ''}
              />
              {formErrors.tiktok_url && <span className="error-message">{formErrors.tiktok_url}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Additional notes (optional)</label>
              <textarea 
                name="additional_notes" 
                value={formData.additional_notes} 
                onChange={handleChange} 
                rows="3"
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AcornsAmbassador;